import { useState, useEffect, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { enqueueSnackbar } from 'notistack';
import { useDispatch, useSelector } from 'react-redux';
import { UnknownAction } from 'redux';
import Axios from 'axios';

import {
  Autocomplete, Backdrop, Box, Button, CircularProgress, Dialog, Grid, IconButton,
  MenuItem, Select, SelectChangeEvent, TextField, Typography
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  DragIndicator as ReorderIcon,
  Delete as RemoveIcon,
  Add as AddIcon,
  ArrowForward as SaveIcon,
} from '@mui/icons-material';

import { useAuth } from 'src/hooks/useAuth';
import { DocumentDeliveryFormatType, ExportMappingType, FieldType, RootState } from 'src/types';
import {
  supplierExportKeys, vatLineExportKeys, esLineExportKeys, vatLineLabelsForExport, esLineLabelsForExport,
  vatLineFields, esLineFields
} from 'src/document-edit/utils';
import { axiosHeaders } from 'src/utils/helpers';
import { fetchHeaderFields, fetchLineFields } from 'src/shared/redux/fields/fieldsActions';
import ConfigContext from 'src/contexts/ConfigContext';
import styles from './style';
import { dataFormatLabels } from 'src/config';

const dataFormats = [
  {
    key: 'excel',
    label: 'Excel',
  },
  {
    key: 'csv',
    label: 'CSV',
  },
  {
    key: 'dat',
    label: 'DAT',
  },
];

const sections = [
  {
    key: 'header',
    label: 'EXPORT_FIELD_MAPPINGS_HEADER_FIELDS',
  },
  {
    key: 'supplier',
    label: 'EXPORT_FIELD_MAPPINGS_SUPPLIER_FIELDS',
  },
  {
    key: 'line',
    label: 'EXPORT_FIELD_MAPPINGS_LINE_FIELDS',
  },
  {
    key: 'vat',
    label: 'EXPORT_FIELD_MAPPINGS_VAT_LINE_FIELDS',
  },
  {
    key: 'es',
    label: 'EXPORT_FIELD_MAPPINGS_ES_LINE_FIELDS',
  },
];

const linkToDocument: FieldType = {
  key: 'linkToDocument',
  appDbField: 'link_to_document',
  assistantKey: 'link_to_document',
  dataType: 'text',
  label: 'LINK_TO_DOCUMENT',
  exportMapKey: 'LINK_TO_DOCUMENT',
  isAvailable: true,
  position: -1,
};

const linkToDocumentPdf: FieldType = {
  key: 'linkToDocumentPdf',
  appDbField: 'link_to_document_pdf',
  assistantKey: 'link_to_document_pdf',
  dataType: 'text',
  exportMapKey: 'LINK_TO_DOCUMENT_PDF',
  label: 'LINK_TO_DOCUMENT_PDF',
  isAvailable: true,
  position: -1,
};

const documentOwner: FieldType = {
  key: 'documentOwner',
  appDbField: 'document_owner',
  assistantKey: 'document_owner',
  dataType: 'text',
  exportMapKey: 'DOCUMENT_OWNER',
  label: 'DOCUMENT_OWNER',
  isAvailable: true,
  position: -1,
};

const companyCode: FieldType = {
  key: 'companyCode',
  appDbField: 'company_code',
  assistantKey: 'company_code',
  dataType: 'text',
  exportMapKey: 'INTEGRATION_COMPANY_CODE',
  label: 'INTEGRATION_COMPANY_CODE',
  isAvailable: true,
  position: -1,
};

const fileName: FieldType = {
  key: 'fileName',
  appDbField: 'file_name',
  assistantKey: 'file_name',
  dataType: 'text',
  exportMapKey: 'FILE_NAME',
  label: 'FILE_NAME',
  isAvailable: true,
  position: -1,
};

const uploadedDate: FieldType = {
  key: 'uploadedDate',
  appDbField: 'uploaded_date',
  assistantKey: 'uploaded_date',
  dataType: 'text',
  exportMapKey: 'UPLOADED_DATE',
  label: 'UPLOADED_DATE',
  isAvailable: true,
  position: -1,
};

const metaDataFields = [
  'LINK_TO_DOCUMENT',
  'LINK_TO_DOCUMENT_PDF',
  'DOCUMENT_OWNER'
];

interface PropTypes {
  open: boolean;
  format?: DocumentDeliveryFormatType;
  docType?: string;
  handleClose: (_updated?: boolean, _format?: DocumentDeliveryFormatType) => void;
}

// NOTE: 'section' is also used as the 'entity' in the export mapping. Both mean the same thing.

const ManageExportFieldsNew = ({
  open,
  format,
  docType,
  handleClose,
}: PropTypes) => {
  const { t, ready } = useTranslation();
  const dispatch = useDispatch();
  const { API } = useContext(ConfigContext);
  const { user } = useAuth();

  const isParentDocTypeSame = user?.documentType === user?.parentDocType;

  const currentDocType: string = useSelector((state: RootState) => state.fields.docType);
  const companyDocTypes: Record<string, any>[] = useSelector((state: RootState) => state.docTypes.companyDocTypes);
  const fieldsLoading: boolean = useSelector((state: RootState) => state.fields.fieldsLoading);

  const _headerFields: FieldType[] = useSelector((state: RootState) => state.fields.headerFields);
  const fields: FieldType[] = useMemo(() => _headerFields, [_headerFields]);

  const _lineFields: FieldType[] = useSelector((state: RootState) => state.fields.lineFields);
  const lineFields: FieldType[] = useMemo(() => _lineFields, [_lineFields]);

  const _supplierFields: FieldType[] = useSelector((state: RootState) => state.fields.supplierFields);
  const supplierFields: FieldType[] = useMemo(() => _supplierFields, [_supplierFields]);

  const hasLines = lineFields.length > 0;
  const hasSupplier = fields.some((f) => f.key === 'supplier');
  const hasVat = fields.some((f) => f.key === 'vatAmount');
  const hasES = fields.some((f) => f.key === 'equivalenceSurchargeAmount');

  const [isEdited, setIsEdited] = useState(Boolean(format?.key));
  const [loading, setLoading] = useState(false);
  const [selectedDocType, setSelectedDocType] =
    useState<string | undefined>(docType ? docType : currentDocType !== '' ? currentDocType : user?.documentType);
  const [dataFormat, setDataFormat] = useState(format?.key || 'excel');
  const [selectedSection, setSelectedSection] = useState(sections[0].key);

  const [savedExportFieldMapping, setSavedExportFieldMapping] = useState<ExportMappingType[]>([]);

  // Create a mapping for each field with the exportMapKey as the value
  const [headerExportKeys, setHeaderExportKeys] = useState<Record<string, string>>([
    ...fields,
    linkToDocument,
    linkToDocumentPdf,
    documentOwner,
    companyCode,
    fileName,
    uploadedDate,
  ].reduce((acc, item) => {
    acc[item.key] = item.exportMapKey;
    return acc;
  }, {} as Record<string, string>));
  const [lineExportKeys, setLineExportKeys] = useState(lineFields.reduce((acc, item) => {
    acc[item.key] = item.exportMapKey;
    return acc;
  }, {} as Record<string, string>));

  // const [isApplyToAll, setIsApplyToAll] = useState(false);

  const [mappings, setMappings] = useState<ExportMappingType[]>([]);
  const [headerMappings, setHeaderMappings] = useState(
    mappings.filter(m => m.entity === 'header')
  );
  const [lineMappings, setLineMappings] = useState(
    mappings.filter(m => m.entity === 'line')
  );
  const [vatMappings, setVatMappings] = useState(
    mappings.filter(m => m.entity === 'vat')
  );
  const [esMappings, setEsMappings] = useState(
    mappings.filter(m => m.entity === 'es')
  );
  const [supplierMappings, setSupplierMappings] = useState(
    mappings.filter(m => m.entity === 'supplier')
  );

  useEffect(() => setSelectedDocType(docType ? docType : currentDocType !== '' ? currentDocType : user?.documentType),
    [docType, currentDocType, user]);

  useEffect(() => {
    setIsEdited(Boolean(format?.key));
    setDataFormat(format?.key ? format.key : 'excel');
  }, [format]);

  // useEffect(() => {
  //   if (dataFormat) {
  //     setIsApplyToAll(
  //       Boolean(user?.customisations?.some((c) => c === `applyParentMappings${dataFormat[0].toUpperCase() + dataFormat.slice(1)}`))
  //     );
  //   }
  // }, [dataFormat, user]);

  useEffect(() => {
    if (open) {
      fetchExportFieldMapping(dataFormat, selectedDocType);
    }
  }, [dataFormat, selectedDocType, open]);

  useEffect(() => {
    setHeaderMappings(mappings.filter((m) => m.entity === 'header'));
    setLineMappings(mappings.filter((m) => m.entity === 'line'));
    setVatMappings(mappings.filter((m) => m.entity === 'vat'));
    setEsMappings(mappings.filter((m) => m.entity === 'es'));
    setSupplierMappings(mappings.filter((m) => m.entity === 'supplier'));
  }, [mappings]);

  useEffect(() => {
    setMappings(savedExportFieldMapping);
    // If no fields are available, set default fields, i.e, fields that are available and active
    if (savedExportFieldMapping.length === 0) {
      let newFields = fields.filter((f) => f.isAvailable && f.isActive).map((f) => ({
        procysField: f.exportMapKey,
        field: t(f.label),
        dataType: 'string',
        format: '',
        entity: 'header'
      }));
      if (hasSupplier) {
        newFields = [...newFields, ...supplierFields.filter(f => f.isAvailable && f.isActive).map(f => ({
          procysField: supplierExportKeys[f.key as string],
          field: t(f.label),
          dataType: 'string',
          format: '',
          entity: 'supplier'
        }))];
      }
      if (hasVat) {
        newFields = [...newFields, ...vatLineFields.map((f) => ({
          procysField: f.exportMapKey,
          field: t(vatLineLabelsForExport[f.key as string]),
          dataType: 'string',
          format: '',
          entity: 'vat'
        }))];
      }
      if (hasES) {
        newFields = [...newFields, ...esLineFields.map((f) => ({
          procysField: f.exportMapKey,
          field: t(esLineLabelsForExport[f.key as string]),
          dataType: 'string',
          format: '',
          entity: 'es'
        }))];
      }
      if (hasLines) {
        newFields = [...newFields, ...lineFields.filter((f) => f.isAvailable && f.isActive).map((f) => ({
          procysField: f.exportMapKey,
          field: t(f.label),
          dataType: 'string',
          format: '',
          entity: 'line'
        }))];
      }
      setMappings(newFields);
    }
    setHeaderExportKeys([
      ...fields,
      linkToDocument,
      linkToDocumentPdf,
      documentOwner,
      companyCode,
      fileName,
      uploadedDate,
    ].reduce((acc, item) => {
      acc[item.key] = item.exportMapKey;
      return acc;
    }, {} as Record<string, string>));
    setLineExportKeys(lineFields.reduce((acc, item) => {
      acc[item.key] = item.exportMapKey;
      return acc;
    }, {} as Record<string, string>));
  }, [savedExportFieldMapping, format, dataFormat, fields, lineFields, supplierFields, hasLines, t]);

  const procysFields: Record<string, FieldType[]> = {
    header: [...fields, linkToDocument, linkToDocumentPdf, documentOwner, companyCode, fileName, uploadedDate],
    supplier: supplierFields,
    line: lineFields,
    vat: vatLineFields,
    es: esLineFields
  };

  const exportKeys: Record<string, Record<string, string>> = {
    header: headerExportKeys,
    supplier: supplierExportKeys,
    line: lineExportKeys,
    vat: vatLineExportKeys,
    es: esLineExportKeys
  };

  const sectionMappings: Record<string, ExportMappingType[]> = {
    header: headerMappings,
    supplier: supplierMappings,
    line: lineMappings,
    vat: vatMappings,
    es: esMappings
  };

  const addBtnLabel: Record<string, string> = {
    header: 'EXPORT_FIELD_MAPPINGS_ADD_HEADER_FIELD',
    supplier: 'EXPORT_FIELD_MAPPINGS_ADD_SUPPLIER_FIELD',
    line: 'EXPORT_FIELD_MAPPINGS_ADD_LINE_FIELD',
    vat: 'EXPORT_FIELD_MAPPINGS_ADD_VAT_FIELD',
    es: 'EXPORT_FIELD_MAPPINGS_ADD_ES_FIELD'
  };

  const isManagedByParent = user?.isSubCompany &&
  user?.parent &&
  isParentDocTypeSame &&
  ((dataFormat === 'excel' && user.customisations.includes('applyParentMappingsExcel')) ||
    (dataFormat === 'csv' && user.customisations.includes('applyParentMappingsCsv')) ||
    (dataFormat === 'dat' && user.customisations.includes('applyParentMappingsDat')));

  const fetchExportFieldMapping = async (format: string, documentType: string | undefined) => {
    setLoading(true);
    try {
      const response = await Axios.get(
        `${API.exportFieldMapping}${format}?documentType=${documentType}`,
        axiosHeaders(localStorage.getItem('PROCYS_accessToken'))
      );
      if (response.data.success) {
        if (response.data.data) {
          const sortedMappings = [...response.data.data].sort((a, b) => {
            const posA = a.position !== undefined ? a.position : Number.MAX_SAFE_INTEGER;
            const posB = b.position !== undefined ? b.position : Number.MAX_SAFE_INTEGER;
            return posA - posB;
          });
          setSavedExportFieldMapping(sortedMappings);
          setLoading(false);
          return;
        }
      }
      setSavedExportFieldMapping([]);
      setLoading(false);
    } catch (error) {
      setSavedExportFieldMapping([]);
      setLoading(false);
    }
  };

  const handleChangeAutoComplete = (value: Record<string, any>) => {
    if (isEdited) {
      enqueueSnackbar(t('EXPORT_FIELD_MAPPING_SAVE_BEFORE_FORMAT_CHANGE'), {
        variant: 'error',
        autoHideDuration: 3000
      });
      const el = document.getElementById('company-doc-type');
      if (el) {
        el.blur();
      }
      return;
    } else if (value && (value.documentType)) {
      setSelectedDocType(value.documentType);
      setSelectedSection(sections[0].key);
      dispatch(fetchHeaderFields(user?.companyID || '', value.documentType) as unknown as UnknownAction);
      dispatch(fetchLineFields(user?.companyID || '', value.documentType) as unknown as UnknownAction);
    }
  };

  const handleFormatChange = (event: SelectChangeEvent<string>) => {
    if (isEdited) {
      enqueueSnackbar(t('EXPORT_FIELD_MAPPING_SAVE_BEFORE_FORMAT_CHANGE'), {
        variant: 'error',
        autoHideDuration: 3000
      });
      return;
    }
    setDataFormat(event.target.value as string);
  };

  const handleChange = (i: number, prop: string, val: string, section: string) => {
    setIsEdited(true);
    switch (section) {
      case 'header':
        {
          const newMappings = [...headerMappings];
          newMappings[i] = { ...newMappings[i], [prop]: val };
          setHeaderMappings(newMappings);
        }
        break;
      case 'supplier':
        {
          const newMappings = [...supplierMappings];
          newMappings[i] = { ...newMappings[i], [prop]: val };
          setSupplierMappings(newMappings);
        }
        break;
      case 'line':
        {
          const newMappings = [...lineMappings];
          newMappings[i] = { ...newMappings[i], [prop]: val };
          setLineMappings(newMappings);
        }
        break;
      case 'vat':
        {
          const newMappings = [...vatMappings];
          newMappings[i] = { ...newMappings[i], [prop]: val };
          setVatMappings(newMappings);
        }
        break;
      case 'es':
        {
          const newMappings = [...esMappings];
          newMappings[i] = { ...newMappings[i], [prop]: val };
          setEsMappings(newMappings);
        }
        break;

      default:
    }
  };

  const handleReorder = (source: number, destination: number | undefined, section: string) => {
    if (destination === undefined) {
      return;
    }
    switch (section) {
      case 'header':
        {
          const newMappings = [...headerMappings];
          const [removed] = newMappings.splice(source, 1);
          newMappings.splice(destination, 0, removed);
          setHeaderMappings(newMappings);
        }
        break;
      case 'supplier':
        {
          const newMappings = [...supplierMappings];
          const [removed] = newMappings.splice(source, 1);
          newMappings.splice(destination, 0, removed);
          setSupplierMappings(newMappings);
        }
        break;
      case 'line':
        {
          const newMappings = [...lineMappings];
          const [removed] = newMappings.splice(source, 1);
          newMappings.splice(destination, 0, removed);
          setLineMappings(newMappings);
        }
        break;
      case 'vat':
        {
          const newMappings = [...vatMappings];
          const [removed] = newMappings.splice(source, 1);
          newMappings.splice(destination, 0, removed);
          setVatMappings(newMappings);
        }
        break;
      case 'es':
        {
          const newMappings = [...esMappings];
          const [removed] = newMappings.splice(source, 1);
          newMappings.splice(destination, 0, removed);
          setEsMappings(newMappings);
        }
        break;

      default:
    }
  };

  const removeMapping = (index: number, section: string) => {
    switch (section) {
      case 'header':
        {
          const newMappings = [...headerMappings];
          newMappings.splice(index, 1);
          setHeaderMappings(newMappings);
        }
        break;
      case 'supplier':
        {
          const newMappings = [...supplierMappings];
          newMappings.splice(index, 1);
          setSupplierMappings(newMappings);
        }
        break
      case 'line':
        {
          const newMappings = [...lineMappings];
          newMappings.splice(index, 1);
          setLineMappings(newMappings);
        }
        break;
      case 'vat':
        {
          const newMappings = [...vatMappings];
          newMappings.splice(index, 1);
          setVatMappings(newMappings);
        }
        break;
      case 'es':
        {
          const newMappings = [...esMappings];
          newMappings.splice(index, 1);
          setEsMappings(newMappings);
        }
        break;

      default:
    }
  };

  const handleAddMapping = (section: string) => {
    switch (section) {
      case 'header':
        setHeaderMappings([
          ...headerMappings,
          {
            procysField: '',
            field: '',
            dataType: 'string',
            format: '',
            entity: section,
            documentType: selectedDocType || '',
            position: headerMappings.length + 1
          }
        ]);
        break;
      case 'supplier':
        setSupplierMappings([
          ...supplierMappings,
          {
            procysField: '',
            field: '',
            dataType: 'string',
            format: '',
            entity: section,
            documentType: selectedDocType || '',
            position: supplierMappings.length + 1
          }
        ]);
        break;
      case 'line':
        setLineMappings([
          ...lineMappings,
          {
            procysField: '',
            field: '',
            dataType: 'string',
            format: '',
            entity: section,
            documentType: selectedDocType || '',
            position: lineMappings.length + 1
          }
        ]);
        break;
      case 'vat':
        setVatMappings([
          ...vatMappings,
          {
            procysField: '',
            field: '',
            dataType: 'string',
            format: '',
            entity: section,
            documentType: selectedDocType || '',
            position: vatMappings.length + 1
          }
        ]);
        break;
      case 'es':
        setEsMappings([
          ...esMappings,
          {
            procysField: '',
            field: '',
            dataType: 'string',
            format: '',
            entity: section,
            documentType: selectedDocType || '',
            position: esMappings.length + 1
          }
        ]);
        break;
      default:
    }
  };

  // const onChangeApplyToAll = async (e: ChangeEvent<HTMLInputElement>) => {
  //   setLoading(true);
  //   e.persist();
  //   const { checked } = e.target;
  //   try {
  //     const response = await Axios.put(
  //       `${API.customisations}`,
  //       {
  //         customisation: `applyParentMappings${dataFormat[0].toUpperCase() + dataFormat.slice(1)}`,
  //         enabled: checked
  //       },
  //       axiosHeaders(localStorage.getItem('PROCYS_accessToken'))
  //     );
  //     if (response.data.success) {
  //       enqueueSnackbar(
  //         t(checked ? 'EXPORT_MAPPINGS_APPLY_TO_ALL_SUCCESS' : 'EXPORT_MAPPINGS_UNAPPLY_TO_ALL_SUCCESS'),
  //         {
  //           variant: 'success',
  //           autoHideDuration: 3000
  //         }
  //       );
  //       setIsApplyToAll(checked);
  //       setLoading(false);
  //     }
  //   } catch (e) {
  //     const error = e as CustomError;
  //     let errorMessage = appendContactSupport(
  //       window.config.support_email,
  //       t('EXPORT_MAPPINGS_APPLY_TO_ALL_FAILURE'),
  //       t
  //     );
  //     if (error && error.response && error.response.data) {
  //       errorMessage = t(error.response.data.i18n || error.response.data.message || '');
  //     }

  //     enqueueSnackbar(
  //       appendContactSupport(window.config.support_email, errorMessage, t),
  //       {
  //         variant: 'error',
  //         autoHideDuration: 3000
  //       }
  //     );
  //     setLoading(false);
  //   }
  // };

  const handleSaveMappings = async () => {
    const hasEmpty = [
      ...headerMappings,
      ...supplierMappings,
      ...lineMappings,
      ...vatMappings,
      ...esMappings
    ].some(m => !m.procysField || !m.field);
    if (hasEmpty) {
      enqueueSnackbar(t('EXPORT_FIELD_MAPPINGS_SAVE_EMPTY'), {
        variant: 'error',
        autoHideDuration: 3000
      });
      return;
    }
    setLoading(true);

    const updatedHeaderMappings = headerMappings.map((mapping, index) => ({
      ...mapping,
      id: mapping.id,
      documentType: selectedDocType || '',
      position: index + 1 // Start positions from 1 instead of 0
    }));

    const updatedSupplierMappings = supplierMappings.map((mapping, index) => ({
      ...mapping,
      id: mapping.id,
      documentType: selectedDocType || '',
      position: index + 1 // Start positions from 1 instead of 0
    }));

    const updatedLineMappings = lineMappings.map((mapping, index) => ({
      ...mapping,
      id: mapping.id,
      documentType: selectedDocType || '',
      position: index + 1 // Start positions from 1 instead of 0
    }));

    const updatedVatMappings = vatMappings.map((mapping, index) => ({
      ...mapping,
      id: mapping.id,
      documentType: selectedDocType || '',
      position: index + 1 // Start positions from 1 instead of 0
    }));

    const updatedEsMappings = esMappings.map((mapping, index) => ({
      ...mapping,
      id: mapping.id,
      documentType: selectedDocType || '',
      position: index + 1 // Start positions from 1 instead of 0
    }));

    let reqBody = {
      documentType: selectedDocType,
      mappings: [
        ...updatedHeaderMappings,
        ...updatedSupplierMappings,
        ...updatedLineMappings,
        ...updatedVatMappings,
        ...updatedEsMappings
      ]
    };

    try {
      const response = await Axios.post(
        `${API.exportFieldMapping}${dataFormat}`,
        reqBody,
        axiosHeaders(localStorage.getItem('PROCYS_accessToken'))
      );
      if (response.data.success) {
        setLoading(false);
        setIsEdited(false);
        enqueueSnackbar(t('EXPORT_FIELD_MAPPINGS_SAVE_SUCCESS'), {
          variant: 'success',
          autoHideDuration: 3000
        });
        handleClose(Boolean(format) && Boolean(docType), format);
      } else {
        setLoading(false);
        enqueueSnackbar(t('EXPORT_FIELD_MAPPINGS_SAVE_FAILED'), {
          variant: 'error',
          autoHideDuration: 3000
        });
      }
    } catch (error) {
      setLoading(false);
      enqueueSnackbar(t('EXPORT_FIELD_MAPPINGS_SAVE_FAILED'), {
        variant: 'error',
        autoHideDuration: 3000
      });
    }
  };

  const renderFieldMapping = (procysField: string, field: string, i: number) => (
    <Draggable
      key={procysField ? `${procysField}_${i}` : i.toString()}
      draggableId={
        procysField ? `${procysField}_${i}` : i.toString()
      }
      index={i}
    >
      {draggableProvided => (
        <Grid
          sx={styles.itemRow}
          ref={draggableProvided.innerRef}
          {...draggableProvided.draggableProps}
        >
          <Grid {...draggableProvided.dragHandleProps} sx={styles.dragIconContainer}>
            <ReorderIcon sx={styles.dragIcon} />
          </Grid>
          <Grid sx={styles.inputItem} >
            <TextField
              value={field}
              onChange={(e) => handleChange(i, 'field', e.target.value, selectedSection)}
              variant="outlined"
              fullWidth
              sx={styles.inputText}
            />
          </Grid>
          <Grid sx={styles.inputItem}>
            <Select
              id={`${selectedSection}-procys-field`}
              variant="outlined"
              onChange={(e) => handleChange(i, 'procysField', e.target.value, selectedSection)}
              sx={styles.selectInput}
              value={procysField}
              MenuProps={{
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left'
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left'
                },
              }}
            >
              {procysFields[selectedSection].filter((f) => f.isAvailable).map(f => (
                <MenuItem sx={styles.menuItem} key={f.key} value={exportKeys[selectedSection][f.key]}>
                  {ready && t(f.label)}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <IconButton onClick={() => removeMapping(i, selectedSection)}>
            <RemoveIcon sx={styles.deleteIcon} />
          </IconButton>
        </Grid>
      )}
    </Draggable>
  );

  return (
    <Dialog
      open={open}
      sx={styles.modal}
      fullScreen
      fullWidth
      slots={{ backdrop: 'div' }}
      slotProps={{ backdrop: { onClick: () => { } } }}
    >
      <Grid sx={styles.modalHeader}>
        <IconButton sx={styles.backBtn} onClick={() => handleClose()}>
          <ArrowBackIcon sx={styles.backIcon} />
        </IconButton>
        <Typography sx={styles.headerTitle}>
          {t('EXPORT_FIELD_MAPPINGS_MODAL_TITLE')}
        </Typography>
      </Grid>
      <Grid sx={styles.modalContent} container>
        <Grid item sx={styles.leftPanel} xs={2.5}>
          <Typography sx={styles.dataFormatText}>
            {t('ACCOUNT_SETTINGS_COMPANIES_DOC_TYPE')}
          </Typography>
          <Autocomplete
            id="company-doc-type"
            fullWidth
            onChange={(_, val) => handleChangeAutoComplete(val as unknown as Record<string, any>)}
            value={companyDocTypes.find((d) => d.documentType === selectedDocType) || null}
            options={companyDocTypes.filter((d) => d.hasFields)}
            getOptionLabel={(option) => typeof option === 'string' ? option : t(option.name)}
            getOptionKey={(option) => option.documentType}
            filterOptions={(options, state) => options.filter((option) => {
              if (option.documentType && option.name) {
                return option.documentType.toLowerCase().includes(state.inputValue.toLowerCase())
                  || option.name.toLowerCase().includes(state.inputValue.toLowerCase());
              }
              return false;
            })}
            sx={styles.autoComplete}
            renderInput={(params) => (
              <TextField
                {...params}
                name="company-doc-type"
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                }}
                sx={styles.autoCompleteTextField}
              />
            )}
          />
          <Typography sx={styles.dataFormatText}>
            {t('EXPORT_FIELD_MAPPING_DATA_FORMAT')}
          </Typography>
          <Select
            id="format-change"
            variant="outlined"
            onChange={handleFormatChange}
            sx={styles.selectInput}
            value={dataFormat}
          >
            {dataFormats.map(f => (
              <MenuItem sx={styles.selectItem} key={f.key} value={f.key}>
                {f.label}
              </MenuItem>
            ))}
          </Select>
          <Box sx={styles.sectionsContainer}>
            {sections.map(section => {
              if (!hasSupplier && section.key === 'supplier') {
                return null;
              }
              if (!hasVat && section.key === 'vat') {
                return null;
              }
              if (!hasES && section.key === 'es') {
                return null;
              }
              if (!hasLines && section.key === 'line') {
                return null;
              }
              return (
                <Box
                  key={section.key}
                  sx={selectedSection === section.key ? styles.sectionContainerActive : styles.sectionContainer}
                  onClick={() => setSelectedSection(section.key)}
                >
                  <Typography sx={selectedSection === section.key ? styles.sectionTextActive : styles.sectionText}>
                    {t(section.label)}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Grid>
        <Grid item sx={styles.contentContainer} xs={9.5}>
        {isManagedByParent
          ? <Grid style={styles.managedByParentContainer}>
              <Typography style={styles.managedByParent}>
                {t('EXPORT_FIELD_MAPPINGS_MANAGED_BY_PARENT', {
                  dataFormat: dataFormatLabels[dataFormat]
                })}
              </Typography>
            </Grid>
          : <>
              <Typography sx={styles.contentTitle}>
                {t(sections.find(e => e.key === selectedSection)?.label || '')}
              </Typography>
              <Grid sx={styles.columnHeaderContainer}>
                <Box sx={styles.columnHeaderFieldsContainer}>
                  <Typography sx={styles.columnHeaderText}>{t('MANAGE_EXPORT_FIELDS_COLUMN_HEADER_FIELDS', {
                    format: dataFormats.find((d) => d.key === dataFormat)?.label
                  })}</Typography>
                </Box>
                <Box sx={styles.columnHeaderFieldsContainer}>
                  <Typography sx={styles.columnHeaderText}>{t('MANAGE_EXPORT_FIELDS_COLUMN_HEADER_PROCYS_FIELDS')}</Typography>
                </Box>
              </Grid>
              <DragDropContext onDragEnd={(e) => handleReorder(e?.source?.index, e?.destination?.index, selectedSection)}>
                <Droppable droppableId="droppable" direction="vertical">
                  {droppableProvided => (
                    <Grid
                      ref={droppableProvided.innerRef}
                      {...droppableProvided.droppableProps}
                      sx={styles.mappingsContainer}
                    >
                      {sectionMappings[selectedSection].map((item, i) => {
                        const { procysField, field } = item;
                        if (metaDataFields.includes(procysField)) return null;
                        return renderFieldMapping(procysField, field, i);
                      })}
                      {/* Render the meta fields only for header */}
                      {selectedSection === 'header' && (
                        <Grid sx={styles.metaFieldsContainer}>
                          {sectionMappings[selectedSection].map((item, i) => {
                            const { procysField, field } = item;
                            if (metaDataFields.includes(procysField)) {
                              return renderFieldMapping(procysField, field, i);
                            }
                          })}
                        </Grid>)}
                      {droppableProvided.placeholder}
                    </Grid>
                  )}
                </Droppable>
              </DragDropContext>
              <Box sx={styles.addMappingBtnContainer}>
                <Button
                  onClick={() => handleAddMapping(selectedSection)}
                  sx={styles.addMappingBtn}
                  startIcon={<AddIcon sx={styles.addMappingIcon} />}
                >
                  {t(addBtnLabel[selectedSection])}
                </Button>
              </Box>
            </>
        }
        </Grid>
      </Grid>
      <Grid sx={styles.modalFooter}>
        {/* {!user?.isSubCompany && <FormControlLabel
          sx={styles.formControlLabel}
          control={<Switch checked={isApplyToAll} onChange={(e) => onChangeApplyToAll(e)} />}
          label={t('MANAGE_FIELDS_APPLY_TO_ALL')}
          disabled={loading}
        />} */}
        <Button
          variant="text"
          sx={styles.cancelBtn}
          onClick={() => handleClose()}
        >
          {ready && t('EXPORT_FIELD_MAPPINGS_CANCEL')}
        </Button>
        <Button
          variant="contained"
          sx={styles.submitBtn}
          onClick={handleSaveMappings}
          endIcon={<SaveIcon sx={styles.saveIcon} />}
        >
          {ready && t('EXPORT_FIELD_MAPPINGS_SAVE')}
        </Button>
      </Grid>
      <Backdrop open={loading || fieldsLoading} sx={{ zIndex: 111111, color: '#ffffff' }}>
        <CircularProgress />
      </Backdrop>
    </Dialog>
  );
};

export default ManageExportFieldsNew;
